import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';

/**
 * Generalized client-side collector for the top-of-page "Required Fields" section (FR-9).
 * Any `RequiredLabeledItem` instance anywhere under a `RequiredFieldsProvider` can report
 * itself as missing/present via `registerRequiredField`/`unregisterRequiredField`;
 * `RequiredFieldsSummary` reads the aggregated map to render the grouped summary.
 *
 * This replaces the earlier backend-`missingRequiredFields`-driven approach entirely —
 * required-field detection is now 100% client-side, reusing the exact same "is this field
 * missing" logic each field's inline display (`RequiredLabeledItem`) already computes.
 *
 * `useRequiredFieldsRegistry()` returns `null` when no provider is present (rather than
 * throwing), so `RequiredLabeledItem`/`RequiredFieldsSummary` remain safe to render in
 * isolation (e.g. existing unit tests, Storybook) without requiring every caller to wrap
 * in a provider.
 */
const RequiredFieldsContext = createContext(null);

const RequiredFieldsProvider = ({ children }) => {
  const [entries, setEntries] = useState({});

  const registerRequiredField = useCallback((fieldId, entry) => {
    setEntries((prev) => {
      const existing = prev[fieldId];
      if (existing && existing.section === entry.section && existing.label === entry.label
        && existing.format === entry.format && existing.linkTo === entry.linkTo
        && existing.controlType === entry.controlType && existing.isRangeWarning === entry.isRangeWarning
        && existing.message === entry.message && existing.complete === entry.complete
        && existing.value === entry.value
        && existing.customRowType === entry.customRowType && existing.rowId === entry.rowId
        // excludeFromCount/requiredMessage/isAddButton can change independently of `complete`
        // (e.g. a list-required entry's excludeFromCount flips once a row exists, while it
        // stays "missing" the whole time) -- must be compared too or the update gets dropped.
        && existing.excludeFromCount === entry.excludeFromCount
        && existing.requiredMessage === entry.requiredMessage
        && existing.isAddButton === entry.isAddButton
        && JSON.stringify(existing.editTarget) === JSON.stringify(entry.editTarget)
        && JSON.stringify(existing.options) === JSON.stringify(entry.options)
        && JSON.stringify(existing.bounds) === JSON.stringify(entry.bounds)
        && JSON.stringify(existing.rowProps) === JSON.stringify(entry.rowProps)) {
        return prev;
      }
      return { ...prev, [fieldId]: entry };
    });
  }, []);

  const unregisterRequiredField = useCallback((fieldId) => {
    setEntries((prev) => {
      if (!(fieldId in prev)) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }, []);

  const value = useMemo(() => ({
    entries, registerRequiredField, unregisterRequiredField,
  }), [entries, registerRequiredField, unregisterRequiredField]);

  return (
    <RequiredFieldsContext.Provider value={value}>
      {children}
    </RequiredFieldsContext.Provider>
  );
};

RequiredFieldsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const useRequiredFieldsRegistry = () => useContext(RequiredFieldsContext);

/**
 * Registers a WHOLE ROW (not a single field) into the "Before you submit" panel for EVERY
 * item in `rows` that has EVER been missing (see `isMissing`) since it was first seen here
 * -- e.g. a Consideration or Commission table row where any one of several fields
 * (type/amount/rate, etc.) can be missing. A row the user already filled in completely
 * earlier in the voucher flow (and that's never since become missing again) never shows up
 * in the panel at all, per product feedback (2026-07-31) that already-complete rows
 * shouldn't clutter this panel just because the user revisited the Review screen. Rather
 * than flagging just the missing cell, the entire row is shown using the EXACT same
 * editable row component (and therefore the exact same UX/redux dispatch behavior) the live
 * Financials tab itself renders -- see `RequiredFieldsSummary`'s `CUSTOM_ROW_COMPONENTS`.
 *
 * A row that HAS ever been missing stays registered (with a `complete` flag reflecting
 * whether `isMissing(row)` is still true) for as long as it exists in `rows`, instead of
 * disappearing the instant it's resolved -- so the user can see it flip to a green
 * checkmark and still re-edit it right there, per the 2026-07-31 product decision to bring
 * this back now that the two-column panel layout addresses the original "too much vertical
 * scroll" concern. A row is only ever fully unregistered when it's actually removed from
 * `rows` (e.g. the user deletes it) or on the registering component's own unmount.
 *
 * `getFieldId`/`isMissing`/`buildEntry` are called for every row on every change to `rows`
 * (or the other `deps`).
 */
const useRegisterRequiredRows = (rows, { getFieldId, isMissing, buildEntry }, deps) => {
  const registry = useRequiredFieldsRegistry();
  const registerRequiredField = registry?.registerRequiredField;
  const unregisterRequiredField = registry?.unregisterRequiredField;
  const registeredIdsRef = useRef(new Set());
  // Per-row (keyed by fieldId) "has this row ever been missing" latch -- mirrors
  // `everNeededAttentionRef` in RequiredLabeledItem.js, but keyed per-row since `rows` can
  // grow/shrink/reorder over time (unlike a single field's fixed identity).
  const everMissingRef = useRef(new Map());

  useEffect(() => {
    if (!registerRequiredField || !unregisterRequiredField) return;
    const currentIds = new Set();
    rows.forEach((row) => {
      const fieldId = getFieldId(row);
      currentIds.add(fieldId);
      const missing = isMissing(row);
      if (missing) everMissingRef.current.set(fieldId, true);
      if (!everMissingRef.current.get(fieldId)) return;
      registerRequiredField(fieldId, { ...buildEntry(row), complete: !missing });
    });
    // Unregister any row that was previously registered here but is no longer present
    // (e.g. the user deleted it) -- a row that's merely resolved (still present in `rows`)
    // stays registered above with `complete: true` instead of being removed here. Also
    // drops its "ever missing" latch, in case a future row somehow reuses the same id.
    registeredIdsRef.current.forEach((fieldId) => {
      if (!currentIds.has(fieldId)) {
        unregisterRequiredField(fieldId);
        everMissingRef.current.delete(fieldId);
      }
    });
    registeredIdsRef.current = currentIds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // Unregister only on true unmount (e.g. navigating away from the Review page), using a
  // ref so the cleanup always reads the latest registered id set without re-running (and
  // thus re-unregistering) on every render -- same pattern as RequiredLabeledItem.js.
  // Harmless no-op for any id that was never actually registered (never missing).
  const unmountRef = useRef();
  unmountRef.current = () => {
    if (!unregisterRequiredField) return;
    registeredIdsRef.current.forEach(fieldId => unregisterRequiredField(fieldId));
  };
  useEffect(() => () => unmountRef.current(), []);
};

/**
 * Registers a single READ-ONLY "list is empty" requirement into the "Before you submit"
 * panel -- e.g. "You must add at least one Consideration/Commission/Installment to this
 * voucher" -- which today only renders as an inline banner inside each Financials-tab-
 * mirroring Review component (`ConsiderationReview.js`/`CommissionsReview.js`/
 * `InstallmentsReview.js`) and was never registered into the client-side registry at all.
 * This left the top-of-page panel completely empty for these sections on a brand-new
 * voucher (zero considerations/commissions/installments) until the user manually visited
 * the live Financials tab and added at least one row -- at which point `useRegisterRequiredRows`
 * above would start registering THAT row's own missing/incomplete state, but nothing had
 * ever registered the "zero rows exist at all" case itself (2026-08-04 bug report).
 *
 * Unlike `useRegisterRequiredRows`, there's no individual row to render/edit inline here --
 * adding the first row can only be done on the live tab -- so this registers a read-only
 * entry (no `editTarget`) with a `linkTo` back to that tab, mirroring how other read-only
 * entries (e.g. computed/derived fields) already render in the panel.
 *
 * Mirrors the "ever needed attention" latch pattern used by `RequiredLabeledItem.js` and
 * `useRegisterRequiredRows` above -- once `missing` is `true` while `isReview` is `true`,
 * this field registers and stays registered (flipping `complete` as the list is populated/
 * emptied again), instead of only registering once.
 */
const useRegisterRequiredField = (fieldId, {
  isReview, missing, buildEntry,
}, deps) => {
  const registry = useRequiredFieldsRegistry();
  const registerRequiredField = registry?.registerRequiredField;
  const unregisterRequiredField = registry?.unregisterRequiredField;
  const everNeededAttentionRef = useRef(false);

  useEffect(() => {
    if (!fieldId || !registerRequiredField) return;
    if (isReview && missing) everNeededAttentionRef.current = true;
    if (!everNeededAttentionRef.current) return;
    registerRequiredField(fieldId, { ...buildEntry(), complete: !missing });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const unmountRef = useRef();
  unmountRef.current = () => {
    if (fieldId && unregisterRequiredField) unregisterRequiredField(fieldId);
  };
  useEffect(() => () => unmountRef.current(), []);
};

/**
 * Registers an "Add Row" ACTION entry (e.g. "Add Broker"/"Add Office"/"Add Pool Account"/
 * "Add Consideration"/"Add Installment") into the "Before you submit" panel -- unlike
 * `useRegisterRequiredField` above, this is NOT gated behind an "ever needed attention"
 * latch: it registers/unregisters immediately based on `shouldRegister` alone (2026-08-04
 * product feedback -- the live Financials tab always shows these "Add" buttons regardless
 * of whether the list is empty, so gating them behind "was this ever missing" caused them
 * to vanish entirely on remount once a row already existed, e.g. after navigating away from
 * Review and back). `buildEntry()` is expected to set `customRowType` (routing to
 * `renderCustomRow` in `RequiredFieldsSummary.js`, which renders ONLY the actual "Add"
 * button(s) -- no label/asterisk/checkmark, since the "must add one" requirement itself is
 * still separately reported via `useRegisterRequiredField`), plus `rowGroup`/`isAddButton`
 * so `orderSectionEntries` can position it directly after that group's own rows (e.g. the
 * Broker rows and "Add Broker" button, then Office rows and "Add Office" button, etc.)
 * instead of all "Add" buttons clustering together above every row.
 */
const useRegisterAddRowAction = (fieldId, shouldRegister, buildEntry, deps) => {
  const registry = useRequiredFieldsRegistry();
  const registerRequiredField = registry?.registerRequiredField;
  const unregisterRequiredField = registry?.unregisterRequiredField;

  useEffect(() => {
    if (!fieldId || !registerRequiredField || !unregisterRequiredField) return;
    if (shouldRegister) {
      registerRequiredField(fieldId, buildEntry());
    } else {
      unregisterRequiredField(fieldId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const unmountRef = useRef();
  unmountRef.current = () => {
    if (fieldId && unregisterRequiredField) unregisterRequiredField(fieldId);
  };
  useEffect(() => () => unmountRef.current(), []);
};

export {
  RequiredFieldsProvider, useRequiredFieldsRegistry, useRegisterRequiredRows, useRegisterRequiredField,
  useRegisterAddRowAction,
};
